// =====================================================================
//  Dialogue d'import d'un paquet « easymail-template/1 »
//  ---------------------------------------------------------------------
//  Coller le JSON ou déposer un fichier .easymail.json -> Analyser ->
//  rapport (erreurs / avertissements / conseils + notes de l'agent +
//  résumé de ce qui va se passer) -> Importer.
//  Résout le paquet validé, ou null si l'utilisateur annule. N'écrit
//  rien lui-même : l'appelant applique le paquet.
// =====================================================================

import {IEasymailPackage, PACKAGE_FILE_EXTENSION} from "../../Model/Package/IEasymailPackage.js";
import {IImportContext, PackageValidator} from "../../Tools/Package/PackageValidator.js";
import {IPackageIssue, PackageReport} from "../../Tools/Package/PackageReport.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {Clipboard} from "../../Tools/Clipboard.js";

export interface IPackageImportDialogOptions {
    title: string;
    /** Phrase d'explication affichée en tête (ce que fait l'import ici). */
    intro: string;
    context: IImportContext;
    /** Lignes décrivant l'effet de l'import (création, champs remplacés…). */
    describe?: (pkg: IEasymailPackage) => Array<string>;
    /** Libellé du bouton de validation. */
    confirmText?: string;
}

export class PackageImportDialog {
    #options: IPackageImportDialogOptions;
    #kWindow: kendo.ui.Window | undefined;
    #jqText: JQuery | undefined;
    #jqReport: JQuery | undefined;
    #jqImport: JQuery | undefined;
    #jqCopy: JQuery | undefined;
    #validated: IEasymailPackage | undefined;
    #lastReport: PackageReport | undefined;
    #resolve: (value: IEasymailPackage | null) => void = () => undefined;

    constructor(options: IPackageImportDialogOptions) {
        this.#options = options;
    }

    /** Ouvre le dialogue ; résout le paquet validé, ou null si abandon. */
    public static Open = (options: IPackageImportDialogOptions): Promise<IEasymailPackage | null> =>
        new PackageImportDialog(options).#open();

    #open = (): Promise<IEasymailPackage | null> => new Promise(resolve => {
        this.#resolve = resolve;
        const jqWindow = this.#buildHtml().appendTo(document.body);

        this.#kWindow = jqWindow.kendoWindow({
            title: this.#options.title,
            modal: true,
            width: "820px",
            height: "85vh",
            resizable: true,
            actions: ["Close"],
            close: () => this.#finish(null),
            deactivate: () => this.#kWindow?.destroy(),
        }).data("kendoWindow");

        this.#kWindow.center().open();
        this.#jqText.trigger("focus");
    });

    #buildHtml = (): JQuery => {
        const root = $("<div></div>").css({display: "flex", flexDirection: "column", gap: "8px", height: "100%"});

        $("<p></p>").css({margin: 0}).text(this.#options.intro).appendTo(root);

        this.#jqText = $("<textarea spellcheck='false'></textarea>")
            .addClass("k-input k-textarea")
            .attr("placeholder", `Collez ici le paquet JSON produit par l'agent IA, ou déposez un fichier ${PACKAGE_FILE_EXTENSION}`)
            .css({flex: "1 1 40%", minHeight: "160px", fontFamily: "Consolas, monospace", fontSize: "12px", padding: "6px", resize: "none"})
            .on("input", () => this.#invalidate())
            .on("dragover", (e) => e.preventDefault())
            .on("drop", (e) => {
                e.preventDefault();
                const file = (e.originalEvent as DragEvent).dataTransfer?.files?.[0];
                if (file)
                    this.#loadFile(file).then();
            })
            .appendTo(root);

        const actions = $("<div></div>").css({display: "flex", gap: "8px", alignItems: "center"}).appendTo(root);
        const jqFile = $("<input type='file'>")
            .attr("accept", ".json,application/json")
            .css({display: "none"})
            .on("change", (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file)
                    this.#loadFile(file).then();
                (e.target as HTMLInputElement).value = "";
            })
            .appendTo(actions);
        PackageImportDialog.#button("Charger un fichier…", "base")
            .on("click", () => jqFile.trigger("click"))
            .appendTo(actions);
        PackageImportDialog.#button("Analyser", "primary")
            .on("click", () => this.#analyse().then())
            .appendTo(actions);

        this.#jqReport = $("<div></div>")
            .css({flex: "1 1 45%", overflowY: "auto", border: "1px solid var(--kendo-color-border, #ddd)", borderRadius: "4px", padding: "8px"})
            .append($("<em></em>").text("Le rapport d'analyse s'affichera ici."))
            .appendTo(root);

        const footer = $("<div></div>").css({display: "flex", justifyContent: "flex-end", gap: "8px"}).appendTo(root);
        this.#jqCopy = PackageImportDialog.#button("Copier le rapport", "base")
            .attr("title", "Copier le rapport pour le transmettre à l'agent IA")
            .prop("disabled", true)
            .on("click", () => this.#copyReport().then())
            .appendTo(footer);
        PackageImportDialog.#button("Annuler", "base")
            .on("click", () => this.#kWindow?.close())
            .appendTo(footer);
        this.#jqImport = PackageImportDialog.#button(this.#options.confirmText ?? "Importer", "success")
            .prop("disabled", true)
            .on("click", () => this.#confirm())
            .appendTo(footer);

        return $("<div></div>").append(root);
    };

    #loadFile = async (file: File) => {
        this.#jqText.val(await file.text());
        this.#invalidate();
        await this.#analyse();
    };

    /** Toute modification du texte invalide l'analyse précédente. */
    #invalidate = () => {
        this.#validated = undefined;
        this.#jqImport.prop("disabled", true);
    };

    #analyse = async () => {
        const raw = String(this.#jqText.val() ?? "").trim();
        if (!raw) {
            NotificationHelper.Instance.info("Collez d'abord un paquet ou chargez un fichier.");
            return;
        }
        this.#jqReport.empty().append($("<em></em>").text("Analyse en cours (dont fusion d'essai par le moteur)…"));

        const {report, package: pkg} = await PackageValidator.Validate(raw, this.#options.context);
        this.#lastReport = report;
        this.#renderReport(report, pkg);
        this.#jqCopy.prop("disabled", false);

        this.#validated = report.HasErrors ? undefined : pkg;
        this.#jqImport.prop("disabled", report.HasErrors);
    };

    #renderReport = (report: PackageReport, pkg?: IEasymailPackage) => {
        const r = this.#jqReport.empty();

        const status = report.HasErrors
            ? {text: `Import impossible : ${report.Errors.length} erreur(s) bloquante(s).`, color: "#c62828"}
            : {text: report.Warnings.length ? "Import possible, avec des avertissements à lire." : "Paquet valide.", color: "#2e7d32"};
        $("<p></p>").css({margin: "0 0 8px 0", fontWeight: "bold", color: status.color}).text(status.text).appendTo(r);

        if (!report.HasErrors && pkg && this.#options.describe) {
            const lines = this.#options.describe(pkg);
            if (lines.length) {
                PackageImportDialog.#title("Ce que va faire l'import").appendTo(r);
                const ul = $("<ul></ul>").css({margin: "0 0 8px 0"}).appendTo(r);
                lines.forEach(l => $("<li></li>").text(l).appendTo(ul));
            }
        }

        PackageImportDialog.#issues(r, "Erreurs bloquantes", report.Errors, "#c62828");
        PackageImportDialog.#issues(r, "Avertissements", report.Warnings, "#e65100");
        PackageImportDialog.#issues(r, "Conseils", report.Advices, "#555");

        if (pkg && typeof pkg.notes === "string" && pkg.notes.trim()) {
            PackageImportDialog.#title("Notes de l'agent").appendTo(r);
            $("<pre></pre>")
                .css({whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", background: "rgba(0,0,0,0.04)", padding: "6px"})
                .text(pkg.notes)
                .appendTo(r);
        }
    };

    #copyReport = async () => {
        if (!this.#lastReport)
            return;
        if (await Clipboard.Copy(this.#lastReport.ToText()))
            NotificationHelper.Instance.success("Rapport copié.");
        else
            NotificationHelper.Instance.error("Copie impossible : sélectionnez le rapport à la main.");
    };

    #confirm = () => {
        if (!this.#validated)
            return;
        const pkg = this.#validated;
        this.#finish(pkg);
        this.#kWindow?.close();
    };

    #finish = (value: IEasymailPackage | null) => {
        const resolve = this.#resolve;
        this.#resolve = () => undefined; // une seule résolution
        resolve(value);
    };

    static #title = (text: string): JQuery =>
        $("<div></div>").css({fontWeight: "bold", margin: "8px 0 4px 0"}).text(text);

    static #issues = (container: JQuery, title: string, list: Array<IPackageIssue>, color: string) => {
        if (!list.length)
            return;
        PackageImportDialog.#title(`${title} (${list.length})`).css({color}).appendTo(container);
        const ul = $("<ul></ul>").css({margin: 0, paddingLeft: "20px"}).appendTo(container);
        list.forEach(x => $("<li></li>")
            .append($("<code></code>").text(x.code).css({marginRight: "6px"}))
            .append(document.createTextNode(x.message))
            .appendTo(ul));
    };

    static #button = (text: string, theme: "base" | "primary" | "success"): JQuery =>
        $("<button type='button'></button>")
            .addClass(`k-button k-button-md k-rounded-md k-button-solid k-button-solid-${theme}`)
            .text(text);
}
